<template>
<div v-loading="isloading">
  <!-- 임계치 분석 정보 (상한, 하한) -->
  <div class="condition-set-box mr-b25">
    <span class="high">
      [{{objInfo.objName}}] {{objInfo.rsctypeName}} > {{objInfo.rscName}} ({{objInfo.rscKey}})의 권장 임계치는
      <div class="mr-l4 round-box-info-text red">상한</div> {{formatterForUpperValue(upperLimit, objInfo.rscUnit)}}
      <div class="mr-l4 round-box-info-text blue">하한</div> {{formatterForUnderValue(underLimit, objInfo.rscUnit)}} 입니다.
    </span>
  </div>
  <!-- 임계치 분석 결과 -->
  <el-row class="title-bar" type="flex" justify="space-between">
    <div class="vertical-center-box">
      <span class="title-h4 mr-r10">이벤트 임계치 분석 결과 [{{analysisDateRange}}]</span>
    </div>
  </el-row>
  <div class="mr-b25">
    <highcharts :options="options" ref="thresholdChart" />
  </div>
  <!-- 이벤트 임계치 설정 -->
  <el-row class="title-bar" type="flex" justify="space-between">
    <div class="vertical-center-box">
      <span class="title-h4 mr-r10">이벤트 임계치 설정</span>
    </div>
    <div class="vertical-center-box">
      <el-button type="secondary" class="mr-r4" :disabled="isEmptyCondition" @click="previewThreshold">미리보기</el-button>
      <div v-if="isAdmin">
        <el-button type="primary" :disabled="isEmptyCondition" @click="saveEventThresholdInfo">저장</el-button>
        <el-button :disabled="isEmptyCondition" @click="reloadGuideInfo">취소</el-button>
      </div>
    </div>
  </el-row>
  <el-table-wrapper :data="guideResultData" :columns="guideResultColumn" ref="thresholdTable" :show-custom-header="false" :pagination="false" style="width:100%;">
    <template slot-scope="scope" slot="eventlevel-slot">
      <w-eventlevel-cell :eventlevel-id="scope.row.eventlevelId"/>
    </template>
    <template slot-scope="scope" slot="current-condition-slot">
      <div v-if="scope.row.dynamicApplyFlag === 'Y'">
        <div v-if="scope.row.staticApplyFlag === 'Y'" class="round-box-info-text red">고정 + 동적</div>
        <div v-else class="round-box-info-text blue">
          동적
        </div>
      </div>
      {{scope.row.eventCondition}}
    </template>
    <template slot-scope="scope" slot="change-condition-slot">
      <el-input :value="scope.row.changeEventCondition" v-model="scope.row.changeEventCondition"></el-input>
    </template>
  </el-table-wrapper>
</div>
</template>

<script>
import moment from 'moment';
import { booleanDefine } from '@/utils/boolean';
import msgUtil from '@/utils/message';
import { mapGetters } from 'vuex';
import { isNil, cloneDeep, concat, isEmpty, findIndex, ceil, find, forEach } from 'lodash';
import EventGuideSettingsAPI from '@/api/settings/common/event-guide-settings';
import WEventlevelCell from '@/components/table/EventlevelCell';
import { TIME, INTERVAL_FIVE_MINUTE } from '@/constants/date';
import { calcUpperThreshold, formatterForUpperValue, formatterForUnderValue } from '@/views/settings/operation/BasicInfoSettings/EventPredictSetting/GuideSetting/utils/threshold-guide';

const chartOptions = {
  credits: {
    enabled: false,
  },
  chart: {
    type: 'scatter',
    height: 250,
  },
  title: {
    text: undefined,
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    type: 'category',
    alignTick: false,
    startOnTick: true,
  },
  yAxis: {
    alignTick: false,
    title: {
      text: '',
    },
  },
  series: [{
    turboThreshold: 10000,
    data: [],
  }],
};

export default {
  name: 'WThresholdAnalysisRsltComponent',
  components: {
    WEventlevelCell,
  },
  props: {
    selectObjThreshold: Object,
    isGuideRsltType: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      booleanDefine,
      TIME,
      INTERVAL_FIVE_MINUTE,
      options: cloneDeep(chartOptions),
      isloading: false,
      upperLimit: null,
      underLimit: null,
      defaultPlotLines: [],
      objInfo: {},
      isEmptyCondition: false,
      analysisDateRange: '',
      chartData: null,
      maximumValue: null,
      minimumValue: null,
      guideResultData: [],
      convertConditionInfo: {},
      guideResultColumn: [
        {
          prop: 'eventlevelName',
          remoteProp: 'eventlevelId',
          label: '이벤트 등급',
          headerAlign: 'center',
          scopedSlot: 'eventlevel-slot',
          width: 100,
        },
        {
          prop: 'eventMessage',
          label: '이벤트 메시지',
          headerAlign: 'center',
          sortable: false,
          searchable: false,
        },
        {
          prop: 'eventCondition',
          label: '이벤트 판단 조건(현재)',
          headerAlign: 'center',
          scopedSlot: 'current-condition-slot',
          sortable: false,
          searchable: false,
        },
      ],
    };
  },
  computed: {
    /**
     * 계정 어드민 권한 여부
     * @author yelim
     * @return {Boolean}
     */
    isAdmin() {
      return this.userInfo.managerType === 'ADMIN';
    },
    ...mapGetters(['userInfo']),
  },
  watch: {
    selectObjThreshold: {
      handler(value) {
        if (!isNil(value)) {
          const selectedObj = value;
          this.objInfo = selectedObj;
          // 임계치 가이드 결과로 제공되는 경우
          if (this.isGuideRsltType) {
            this.options = cloneDeep(chartOptions);
            this.upperLimit = selectedObj.upperLimit;
            this.underLimit = selectedObj.underLimit;
            this.analysisDateRange = selectedObj.analysisRange;
          }
          this.loadChartInfo();
        }
      },
      immediate: true,
    },
    /**
     * 화면 전환 도중 계정 타입이 바뀌는 경우를 대비
     */
    isAdmin: {
      immediate: true,
      handler(value) {
        this.addCheckColumn(value);
      },
    },
  },
  methods: {
    formatterForUpperValue,
    formatterForUnderValue,
    /**
     * 관리자 권한인 경우에만 이벤트 판단 조건(변경) 컬럼을 추가한다.
     * @author yelim
     * @param  {Boolean} isAdmin [description]
     */
    addCheckColumn(isAdmin) {
      if (isAdmin) {
        const columns = cloneDeep(this.guideResultColumn);
        const eventJudgeConditionCol = {
          prop: 'changeEventCondition',
          label: '이벤트 판단 조건(변경)',
          headerAlign: 'center',
          scopedSlot: 'change-condition-slot',
        };
        const concatCols = concat(columns, [eventJudgeConditionCol]);
        this.guideResultColumn = concatCols;
      }
    },
    /**
     * 자원에 설정된 이벤트 판단 조건 목록을 조회한다.
     * @author yelim
     * @return {Boolean} [description]
     */
    loadThresholdAnalysisInfo() {
      return new Promise((resolve) => {
        const targetObjInfo = this.objInfo;
        const params = {
          objId: targetObjInfo.objId,
          managetypeId: targetObjInfo.managetypeId,
          rsctypeId: targetObjInfo.rsctypeId,
          rscId: targetObjInfo.rscId,
          rsctypeKey: targetObjInfo.rscKey,
        };
        EventGuideSettingsAPI.getSettingThresholdInfo(params).then((response) => {
          if (response.data.success) {
            const result = response.data.data;
            if (!isEmpty(result)) {
              this.convertConditionInfo = this.setEventCondition(result, false);
              const eventLevelIds = Object.keys(this.convertConditionInfo);
              eventLevelIds.forEach((eventlevelId) => {
                const findEventInfo = find(result, (resultEventlevel) => resultEventlevel.eventlevelId === eventlevelId);
                if (!isNil(findEventInfo)) {
                  findEventInfo.changeEventCondition = this.convertConditionInfo[eventlevelId].changeEventCondition;
                }
              });
              this.isEmptyCondition = false;
            } else {
              // 사용자 자원인 경우 이벤트 판단 조건 목록이 존재하지 않는 경우 예외처리
              this.isEmptyCondition = true;
              this.convertConditionInfo = {};
            }
            this.guideResultData = result;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트/알람 설정', false));
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트/알람 설정', false));
          throw new Error(e);
        }).finally(() => {
          resolve(true);
        });
      });
    },
    /**
     * 이벤트 임계치 분석 결과를 조회한다.
     * @author yelim
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    loadChartInfo() {
      const targetObjInfo = this.objInfo;
      const params = {
        hashkeyIdString: targetObjInfo.hashkeyIdString,
      };
      // 결과팝업인 경우 필요한 파라미터
      if (this.isGuideRsltType) {
        params.startDatetime = targetObjInfo.analysisStartDatetime;
        params.endDatetime = targetObjInfo.analysisEndDatetime;
        params.upperLimit = this.upperLimit;
        params.underLimit = this.underLimit;
      }
      this.isloading = true;
      EventGuideSettingsAPI.loadThresholdGuideChart(params).then((response) => {
        if (response.data.success) {
          const result = response.data.data;
          this.chartData = result.chartInfo;
          this.maximumValue = result.limitInfo.maximumValue;
          this.minimumValue = result.limitInfo.minimumValue;
          if (!this.isGuideRsltType) {
            const startDatetime = result.limitInfo.startDatetime;
            const endDatetime = result.limitInfo.endDatetime;
            this.upperLimit = result.limitInfo.upperLimit;
            this.underLimit = result.limitInfo.underLimit;
            this.analysisDateRange = `${moment(new Date(startDatetime)).format('YYYY-MM-DD')} ~ ${moment(new Date(endDatetime)).format('YYYY-MM-DD')}`;
          }
          this.loadThresholdAnalysisInfo().then(() => {
            this.makeChartSkeleton();
          });
        } else {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 임계치 분석 결과', false));
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 임계치 분석 결과', false));
        throw new Error(e);
      }).finally(() => {
        this.isloading = false;
      });
    },
    /**
     * 이벤트 임계치를 수정할 수 있는 판단 조건을 계산한다.
     * @author yelim
     * @param  {[type]} eventCondition [description]
     */
    setEventCondition(eventCondition, changeFlag) {
      const levelCondition = {};
      eventCondition.forEach((eventInfo) => {
        const eventStaticCondition = (changeFlag) ? eventInfo.changeEventCondition : eventInfo.staticEventCondition;
        const eventlevelId = eventInfo.eventlevelId;
        const eventlevelColorBg = eventInfo.eventlevelColorBg;
        let logicalCondition;
        const conditionValue = [];
        if (!isNil(eventStaticCondition)) {
          // 이벤트 판단 조건이 단일 조건이 아닌 경우를 판단한다.
          if (eventStaticCondition.indexOf('||') >= 0) {
            logicalCondition = '||';
          } else if (eventStaticCondition.indexOf('&&') >= 0) {
            logicalCondition = '&&';
          } else {
            logicalCondition = null;
          }
          if (!isEmpty(logicalCondition)) {
            const doubleEventCondition = eventStaticCondition.split(logicalCondition);
            doubleEventCondition.forEach((item) => {
              const valueInfo = this.separationConditionValue(eventCondition, item, eventlevelId, eventInfo.rscUnit);
              valueInfo.eventlevelColorBg = eventlevelColorBg;
              if (!isEmpty(valueInfo)) {
                conditionValue.push(valueInfo);
              }
            });
          } else {
            const valueInfo = this.separationConditionValue(eventCondition, eventStaticCondition, eventlevelId, eventInfo.rscUnit);
            valueInfo.eventlevelColorBg = eventlevelColorBg;
            if (!isEmpty(valueInfo)) {
              conditionValue.push(valueInfo);
            }
          }
          levelCondition[eventlevelId] = conditionValue;
          levelCondition[eventlevelId].eventlevelName = eventInfo.eventlevelName;
          if (!isEmpty(logicalCondition)) {
            levelCondition[eventlevelId].changeEventCondition = `${conditionValue[0].changeEventCondition}${logicalCondition}${conditionValue[1].changeEventCondition}`;
          } else {
            levelCondition[eventlevelId].changeEventCondition = conditionValue[0].changeEventCondition;
          }
        }
      });
      return levelCondition;
    },
    separationConditionValue(eventCondition, value, eventlevelId, rscUnit) { // eventCondition 파라미터 임시 추가
      let splitEventCondition = {};
      let separator;
      if (value.indexOf('<=') > 0) {
        separator = '<=';
      } else if (value.indexOf('<') > 0) {
        separator = '<';
      } else if (value.indexOf('>=') > 0) {
        separator = '>=';
      } else if (value.indexOf('>') > 0) {
        separator = '>';
      } else {
        separator = null;
      }
      if (!isNil(separator)) {
        const conditionValues = value.split(separator);
        const targetValue = conditionValues[1].trim();
        splitEventCondition = {
          eventCondition: value,
          separator,
          originJudgeValue: Number(targetValue),
        };
        const index = findIndex(eventCondition, (data) => data.eventlevelId === eventlevelId);
        const changeJudgeValue = calcUpperThreshold(this.upperLimit, rscUnit, (eventCondition.length - index)).toFixed(2);
        splitEventCondition.changeEventCondition = `${conditionValues[0]}${separator} ${changeJudgeValue}`;
        splitEventCondition.changeJudgeValue = changeJudgeValue !== null ? changeJudgeValue : (Number(targetValue));
      }
      return splitEventCondition;
    },
    makeChartSkeleton() {
      const upperLimitValue = this.upperLimit;
      const underLimitValue = this.underLimit;
      // 차트 최대,최소 셋팅
      const maxValue = this.maximumValue;
      const minValue = this.minimumValue;
      // 차트 옵션값
      const chartOptionInfo = cloneDeep(this.options);
      const changeEventCondition = this.convertConditionInfo;
      // 임계치 표현을 위한 데이터
      const plotLinesInfo = [];
      const plotBandsInfo = [];
      // 차트 데이터를 바인딩한다.
      const rawChartData = this.chartData;
      // 2. 조회한 현재 이벤트 판단 조건을 차트에 setting한다.
      if (!isEmpty(changeEventCondition)) {
        forEach(changeEventCondition, (eventInfo) => {
          eventInfo.forEach((doubleCondition) => {
            plotLinesInfo.push({
              color: doubleCondition.eventlevelColorBg,
              width: 2,
              value: doubleCondition.originJudgeValue,
              zIndex: 4,
            });
          });
        });
        plotLinesInfo.sort((a, b) => (a.value < b.value ? 1 : -1));
        if ((upperLimitValue < plotLinesInfo[0].value) && (maxValue < plotLinesInfo[0].value)) {
          chartOptionInfo.yAxis.max = plotLinesInfo[0].value;
        } else if ((upperLimitValue < maxValue) && (plotLinesInfo[0].value < maxValue)) {
          chartOptionInfo.yAxis.max = maxValue;
        } else if (maxValue < upperLimitValue && (plotLinesInfo[0].value < upperLimitValue)) {
          chartOptionInfo.yAxis.max = upperLimitValue;
        } else {
          chartOptionInfo.yAxis.max = null;
        }
        const lineIdx = (plotLinesInfo.length !== 0) ? plotLinesInfo.length - 1 : null;
        if (!isNil(lineIdx)) {
          const lineValue = plotLinesInfo[lineIdx].value;
          if ((underLimitValue > lineValue) && (minValue > lineValue)) {
            chartOptionInfo.yAxis.min = lineValue;
          } else if ((underLimitValue > minValue) && (lineValue > minValue)) {
            chartOptionInfo.yAxis.min = minValue;
          } else if (minValue > underLimitValue && (lineValue > underLimitValue)) {
            chartOptionInfo.yAxis.min = underLimitValue;
          } else {
            chartOptionInfo.yAxis.min = null;
          }
        } else {
          chartOptionInfo.yAxis.min = null;
        }
        chartOptionInfo.yAxis.plotLines = plotLinesInfo;
        this.defaultPlotLines = plotLinesInfo;
      } else {
        if (upperLimitValue < maxValue) {
          chartOptionInfo.yAxis.max = maxValue;
        } else if (upperLimitValue > maxValue) {
          chartOptionInfo.yAxis.max = upperLimitValue;
        } else {
          chartOptionInfo.yAxis.max = upperLimitValue + (upperLimitValue * 0.5);
        }
        if (underLimitValue < minValue) {
          chartOptionInfo.yAxis.min = underLimitValue;
        } else if (underLimitValue > minValue) {
          chartOptionInfo.yAxis.min = minValue;
        } else {
          chartOptionInfo.yAxis.min = underLimitValue - (underLimitValue * 0.5);
        }
        chartOptionInfo.yAxis.plotLines = [];
        this.defaultPlotLines = [];
      }
      // 3. popuptype에 따라 상한.하한값을 차트에 그린다.
      plotBandsInfo.push({
        color: 'rgba(247, 186, 0, 0.1)',
        from: ceil(Number(underLimitValue)),
        to: ceil(Number(upperLimitValue)),
      });
      chartOptionInfo.yAxis.plotBands = plotBandsInfo;
      // 4. 시간에 맞는 값이 존재하는 경우 셋팅한다.
      if (!isNil(rawChartData)) {
        chartOptionInfo.tooltip = {
          formatter() {
            const point = this.point;
            return `시간 : ${point.name}<br>값 : ${point.y}`;
          },
        };
      }
      chartOptionInfo.series[0].data = cloneDeep(rawChartData);
      this.options = cloneDeep(chartOptionInfo);
    },
    /**
     * 변경된 임계치 가이드 설정을 차트에 표시한다.
     * @author yelim
     * @return {[type]} [description]
     */
    previewThreshold() {
      if (this.validateEventConditions() === false) {
        this.$message(msgUtil.getCustomMsgData('입력된 판단 조건 값을 확인해주세요.'));
        return;
      }
      const chartOptionInfo = cloneDeep(this.options);
      const changeThreshold = [];
      const defaultPlotLines = this.defaultPlotLines;
      const changeEventCondition = this.convertConditionInfo;
      this.isloading = true;
      // 2. 조회한 현재 이벤트 판단 조건을 차트에 setting한다.
      if (!isEmpty(changeEventCondition)) {
        forEach(changeEventCondition, (eventInfo) => {
          eventInfo.forEach((doubleCondition) => {
            changeThreshold.push({
              dashStyle: 'dash',
              color: doubleCondition.eventlevelColorBg,
              width: 2,
              value: doubleCondition.originJudgeValue,
              zIndex: 4,
            });
          });
        });
        const concatLines = changeThreshold.concat(defaultPlotLines);
        let lineCnt = null;
        concatLines.sort((a, b) => (a.value < b.value ? 1 : -1));
        if (!isEmpty(concatLines)) {
          lineCnt = concatLines.length - 1;
          chartOptionInfo.yAxis.max = (concatLines[0].value > chartOptionInfo.yAxis.max) ? concatLines[0].value : chartOptionInfo.yAxis.max;
          chartOptionInfo.yAxis.min = (concatLines[lineCnt].value < chartOptionInfo.yAxis.min) ? concatLines[lineCnt].value : chartOptionInfo.yAxis.min;
        }
        chartOptionInfo.yAxis.plotLines = [];
        chartOptionInfo.yAxis.plotLines = concatLines;
      }
      const rawChartData = this.chartData;
      chartOptionInfo.series[0].data = cloneDeep(rawChartData);
      this.options = cloneDeep(chartOptionInfo);
      this.isloading = false;
    },
    /**
     * 변경된 이벤트 판단 조건에 따른 설정의 유효성 검사를 수행한다.
     * @author yelim
     * @return {[type]} [description]
     */
    validateEventConditions() {
      let isAbleSave = true;
      const changeEventConditions = this.guideResultData;
      const targetValidation = this.setEventCondition(changeEventConditions, true);
      if (!isEmpty(targetValidation)) {
        forEach(targetValidation, (condition) => {
          if (isNaN(condition[0].originJudgeValue)) {
            isAbleSave = false;
          }
        });
      } else {
        isAbleSave = false;
      }
      if (isAbleSave && !isEmpty(this.convertConditionInfo)) {
        this.convertConditionInfo = targetValidation;
      }
      return isAbleSave;
    },
    /**
     * 취소 버튼을 누른 경우 임계치 설정 정보를 다시 load한다.
     * @author yelim
     * @return {[type]} [description]
     */
    reloadGuideInfo() {
      this.isloading = true;
      new Promise((resolve) => {
        this.loadThresholdAnalysisInfo().then(() => {
          this.makeChartSkeleton();
          resolve();
        });
      }).then(() => {
        this.isloading = false;
      });
    },
    /**
     * 변경된 판단조건에 따라 이벤트 판단 조건을 설정한다.
     * @author yelim
     * @return {[type]} [description]
     */
    saveEventThresholdInfo() {
      const setEventCondition = [];
      const currentEventCondition = this.guideResultData;
      if (this.validateEventConditions() === false) {
        this.$message(msgUtil.getCustomMsgData('입력된 판단 조건 값을 확인해주세요.'));
      } else {
        currentEventCondition.forEach((eventInfo) => {
          const staticFlag = eventInfo.staticApplyFlag;
          const dynamicFlag = eventInfo.dynamicApplyFlag;
          const defineInfo = this.booleanDefine;
          if (staticFlag === defineInfo.TRUE_Y && dynamicFlag === defineInfo.FALSE_N) {
            eventInfo.eventCondition = eventInfo.changeEventCondition;
            eventInfo.staticEventCondition = eventInfo.changeEventCondition;
            setEventCondition.push(eventInfo);
          } else if (staticFlag === defineInfo.FALSE_N && dynamicFlag === defineInfo.TRUE_Y) {
            eventInfo.eventCondition = `${eventInfo.changeEventCondition} || (${eventInfo.dynamicEventCondition})`;
            eventInfo.staticEventCondition = eventInfo.changeEventCondition;
            eventInfo.staticApplyFlag = defineInfo.TRUE_Y;
            setEventCondition.push(eventInfo);
          } else if (staticFlag === defineInfo.TRUE_Y && dynamicFlag === defineInfo.TRUE_Y) {
            eventInfo.eventCondition = `${eventInfo.changeEventCondition} || (${eventInfo.dynamicEventCondition})`;
            eventInfo.staticEventCondition = eventInfo.changeEventCondition;
            setEventCondition.push(eventInfo);
          }
        });
        EventGuideSettingsAPI.setEventThresholdCondition(setEventCondition).then((response) => {
          if (response.data.success) {
            this.loadThresholdAnalysisInfo().then(() => {
              this.makeChartSkeleton();
            });
            this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '이벤트 임계치 설정', true));
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '이벤트 임계치 설정', false));
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '이벤트 임계치 설정', false));
          throw new Error(e);
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list-info {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    min-height: 50px;
    border: 1px solid #e9e9e9;
    background-color: #f1f1f1;
    padding: 4px 18px;

    .high {
        display: inline-block;
        padding-right: 4px;
        font-size: 14px;
        letter-spacing: -1px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/ResourceSmartAnalyzerPopup/components/ThresholdAnalysisRsltComponent.vue